import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaSave, FaTimes, FaPlus, FaSearch, FaCheck, FaTruck, FaBan, FaExclamationTriangle, FaChevronDown, FaChevronRight, FaSortUp, FaSortDown } from 'react-icons/fa';
import './ClientsInstructions.css';
import Button from './UI/Button';
import { SOUFFRANCE_LANGUAGES } from './ClientEDNDetail';

const ClientsInstructions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [carriers, setCarriers] = useState([]);

  // List state (page, search, size, sort) lives in the URL so browser back
  // restores it after visiting a client — open a client from page 4, come
  // back, land on page 4 (frontend-design.md).
  const [searchParams, setSearchParams] = useSearchParams();
  const PAGE_SIZES = [25, 50, 100];
  const searchQuery = searchParams.get('q') || '';
  const sortDesc = searchParams.get('sort') === '-name';
  const sizeParam = Number(searchParams.get('size'));
  const pageSize = PAGE_SIZES.includes(sizeParam) ? sizeParam : 25;
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const requestedPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const updateListParams = (updates, { replace = false } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace });
  };

  const handleSearchChange = (value) => updateListParams({ q: value, page: null }, { replace: true });
  const handlePageChange = (page) => updateListParams({ page: page <= 1 ? null : page });
  const handlePageSizeChange = (size) => updateListParams({ size: size === 25 ? null : size, page: null });
  const toggleNameSort = () => updateListParams({ sort: sortDesc ? null : '-name' }, { replace: true });
  const [newClient, setNewClient] = useState({
    name: '',
    client_id: '',
    emails: [],
    instructions: '',
    reference: '',
    automation: false,
    disabled_carriers: [],
    aliases: [],
    default_email: '',
    effitrace_url: '',
    souffrance_language: '',
    souffrance_instructions: '',
  });

  const [souffranceConfig, setSouffranceConfig] = useState({ language: '', instructions: '', signature: '' });
  const [souffranceConfigured, setSouffranceConfigured] = useState(false);
  const [showSouffranceSettings, setShowSouffranceSettings] = useState(false);
  const [souffranceSaving, setSouffranceSaving] = useState(false);
  const [souffranceFeedback, setSouffranceFeedback] = useState(null);

  // Filter → sort → paginate, all client-side (the API returns the full list)
  const filteredClients = clients.filter(client => {
    const q = searchQuery.toLowerCase();
    return client.name.toLowerCase().includes(q) ||
      (client.client_id || '').toLowerCase().includes(q) ||
      (client.emails || []).some(e => e.toLowerCase().includes(q));
  });

  const sortedClients = [...filteredClients].sort((a, b) => {
    const cmp = (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' });
    return sortDesc ? -cmp : cmp;
  });

  const totalPages = Math.max(1, Math.ceil(sortedClients.length / pageSize));
  const currentPage = Math.min(requestedPage, totalPages);
  const paginatedClients = sortedClients.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Numbered pager: first, last, and a window around the current page
  const getPageItems = () => {
    const anchors = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const pages = [...anchors].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    const items = [];
    let prev = 0;
    pages.forEach(p => {
      if (p - prev > 1) items.push(`gap-${p}`);
      items.push(p);
      prev = p;
    });
    return items;
  };

  const renderPager = () => (
    <nav className="clients-pager" aria-label={t('clientsList.pagination', 'Pagination')}>
      <button
        type="button"
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
        aria-label={t('common.previous')}
      >
        ‹
      </button>
      {getPageItems().map(item => (
        typeof item === 'number' ? (
          <button
            key={item}
            type="button"
            className={item === currentPage ? 'active' : ''}
            aria-current={item === currentPage ? 'page' : undefined}
            onClick={() => handlePageChange(item)}
          >
            {item}
          </button>
        ) : (
          <span key={item} className="pager-ellipsis" aria-hidden="true">…</span>
        )
      ))}
      <button
        type="button"
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
        aria-label={t('common.next')}
      >
        ›
      </button>
    </nav>
  );

  useEffect(() => {
    fetchClients();
    fetchCarriers();
    fetchSouffranceConfig();
  }, []);

  const fetchSouffranceConfig = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/souffrance-config', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSouffranceConfig({
        language: response.data.language || '',
        instructions: response.data.instructions || '',
        signature: response.data.signature || '',
      });
      setSouffranceConfigured(!!(response.data.language || response.data.instructions || response.data.signature));
    } catch (error) {
      console.error('Failed to fetch souffrance settings:', error);
    }
  };

  const handleSaveSouffranceConfig = async () => {
    setSouffranceSaving(true);
    setSouffranceFeedback(null);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.put(
        '/api/logistics/souffrance-config',
        {
          language: souffranceConfig.language || null,
          instructions: souffranceConfig.instructions || null,
          // The backend keeps the stored signature on null: '' clears it.
          signature: souffranceConfig.signature.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSouffranceConfigured(!!(response.data.language || response.data.instructions || response.data.signature));
      setSouffranceFeedback({
        type: 'success',
        message: t('souffranceSettings.saved', 'Souffrance settings saved.'),
      });
    } catch (error) {
      console.error('Failed to save souffrance settings:', error);
      const detail = error.response?.data?.detail;
      setSouffranceFeedback({
        type: 'error',
        message: typeof detail === 'string'
          ? detail
          : t('souffranceSettings.saveError', 'Failed to save souffrance settings.'),
      });
    } finally {
      setSouffranceSaving(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/clients-edn', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCarriers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/logistics/carriers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarriers(response.data);
    } catch (error) {
      console.error('Failed to fetch carriers:', error);
    }
  };

  const handleCloseCreateModal = () => {
    if (creating) return;
    setShowCreateModal(false);
    setCreateError(null);
  };

  const handleCreateClient = async () => {
    if (!newClient.name.trim()) {
      setCreateError(t('clientsInstructions.nameRequired', 'Le nom du client est obligatoire.'));
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const token = localStorage.getItem('access_token');
      const emailRouting = {};
      if (newClient.default_email) emailRouting.default_email = newClient.default_email;
      const payload = {
        name: newClient.name.trim(),
        client_id: newClient.client_id,
        emails: newClient.emails.filter((email) => email.trim() !== ''),
        instructions: newClient.instructions,
        reference: newClient.reference,
        automation: newClient.automation,
        disabled_carriers: newClient.disabled_carriers,
        aliases: newClient.aliases.filter((alias) => alias.trim() !== ''),
        email_routing: Object.keys(emailRouting).length > 0 ? emailRouting : null,
        effitrace_url: newClient.effitrace_url || null,
        souffrance_language: newClient.souffrance_language || null,
        souffrance_instructions: newClient.souffrance_instructions || null,
      };
      await axios.post('/api/logistics/clients-edn', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      setNewClient({
        name: '',
        client_id: '',
        emails: [],
        instructions: '',
        reference: '',
        automation: false,
        disabled_carriers: [],
        aliases: [],
        default_email: '',
        effitrace_url: '',
        souffrance_language: '',
        souffrance_instructions: '',
      });
      fetchClients();
    } catch (error) {
      console.error('Failed to create client:', error);
      const detail = error.response?.data?.detail;
      setCreateError(typeof detail === 'string'
        ? detail
        : t('clientsInstructions.createError', 'Échec de la création du client.'));
    } finally {
      setCreating(false);
    }
  };

  const handleClientClick = (client) => {
    navigate(`/clients-instructions/${client.id}`);
  };

  if (isLoading) {
    return (
      <div className="clients-instructions-container">
        <div className="clients-card">
          <div className="clients-skeleton" aria-label={t('clientsInstructions.loadingTitle', 'Loading clients')} aria-busy="true">
            {Array.from({ length: 10 }).map((_, index) => (
              <div key={index} className="clients-skeleton-row" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="clients-instructions-container">
      <header className="clients-page-head">
        <h1>{t('clientsInstructions.pageTitle', 'Clients B2B')}</h1>
        <p>{t('clientsInstructions.pageSubtitle', 'Les entreprises pour lesquelles vous expédiez. Un ticket est rattaché à son client B2B à l’arrivée (domaine e-mail, alias, références) ; ses instructions et ses transporteurs autorisés s’appliquent ensuite.')}</p>
      </header>

      <div className="souffrance-settings-panel">
        <button
          type="button"
          className="souffrance-settings-header"
          onClick={() => setShowSouffranceSettings((v) => !v)}
          aria-expanded={showSouffranceSettings}
        >
          <div className="souffrance-settings-title">
            <FaExclamationTriangle className="souffrance-settings-icon" />
            <div>
              <h3>{t('souffranceSettings.title', 'General souffrance instructions')}</h3>
              <p>
                {t(
                  'souffranceSettings.subtitle',
                  'Applied to every souffrance notification, for all clients. Open a client\'s page to override the language or add client-specific instructions.'
                )}
              </p>
            </div>
          </div>
          <div className="souffrance-settings-meta">
            <span className={`souffrance-settings-badge ${souffranceConfigured ? 'configured' : ''}`}>
              {souffranceConfigured
                ? t('souffranceSettings.configured', 'Configured')
                : t('souffranceSettings.notConfigured', 'Default')}
            </span>
            {showSouffranceSettings ? <FaChevronDown /> : <FaChevronRight />}
          </div>
        </button>
        {showSouffranceSettings && (
          <div className="souffrance-settings-body">
            {souffranceFeedback && (
              <div className={`souffrance-settings-feedback ${souffranceFeedback.type}`}>
                {souffranceFeedback.message}
              </div>
            )}
            <div className="form-group">
              <label>{t('clientEDNDetail.souffranceLanguage', 'Message language')}:</label>
              <select
                value={souffranceConfig.language}
                onChange={(e) => {
                  setSouffranceConfig({ ...souffranceConfig, language: e.target.value });
                  setSouffranceFeedback(null);
                }}
                className="ai-agent-input"
              >
                <option value="">{t('clientEDNDetail.souffranceLanguageDefault', 'Default (French)')}</option>
                {SOUFFRANCE_LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>
                    {t(`clientEDNDetail.languages.${lang}`, lang)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('souffranceSettings.generalInstructions', 'General instructions')}:</label>
              <textarea
                value={souffranceConfig.instructions}
                onChange={(e) => {
                  setSouffranceConfig({ ...souffranceConfig, instructions: e.target.value });
                  setSouffranceFeedback(null);
                }}
                rows="5"
                placeholder={t('clientEDNDetail.souffranceInstructionsPlaceholder', 'E.g. always mention our support phone number; ask for a relay point choice when delivery failed...')}
                className="ai-agent-textarea"
              />
            </div>
            <div className="form-group">
              <label>{t('souffranceSettings.signature', 'Signature')}:</label>
              <input
                type="text"
                value={souffranceConfig.signature}
                maxLength={300}
                onChange={(e) => {
                  setSouffranceConfig({ ...souffranceConfig, signature: e.target.value });
                  setSouffranceFeedback(null);
                }}
                placeholder={t('souffranceSettings.signaturePlaceholder', 'E.g. Customer service, Your company')}
                className="ai-agent-input"
              />
              <small className="souffrance-settings-hint">
                {t('souffranceSettings.signatureHelp', 'Ends every carrier notice the platform sends your clients. Left empty, notices are sent unsigned.')}
              </small>
            </div>
            <div className="souffrance-settings-actions">
              <Button variant="primary" onClick={handleSaveSouffranceConfig} disabled={souffranceSaving}>
                <FaSave /> {souffranceSaving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="clients-card">
        <div className="clients-card-header">
          <div className="clients-search">
            <FaSearch className="clients-search-icon" aria-hidden="true" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('common.search')}
            />
            {searchQuery && (
              <button
                type="button"
                className="clients-search-clear"
                onClick={() => handleSearchChange('')}
                aria-label={t('clearSearch', 'Effacer la recherche')}
              >
                <FaTimes />
              </button>
            )}
          </div>
          <span className="clients-count">
            {sortedClients.length === 1
              ? t('clientsList.countOne', '1 client')
              : t('clientsList.countMany', '{{total}} clients', { total: sortedClients.length.toLocaleString('fr-FR') })}
          </span>
          <button
            type="button"
            className="u-btn u-btn--primary"
            data-tour="clients-create"
            onClick={() => setShowCreateModal(true)}
          >
            <FaPlus /> {t('clientsInstructions.createNew')}
          </button>
        </div>

        <div className="clients-toolbar">
          <label className="clients-page-size">
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZES.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            {t('clientsList.perPage', 'par page')}
          </label>
          {renderPager()}
        </div>

        {paginatedClients.length > 0 ? (
          <div className="clients-table-scroll">
            <table className="clients-table">
              <colgroup>
                <col />
                <col style={{ width: 90 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 44 }} />
              </colgroup>
              <thead>
                <tr>
                  <th
                    className="sortable"
                    onClick={toggleNameSort}
                    aria-sort={sortDesc ? 'descending' : 'ascending'}
                  >
                    {t('clientsInstructions.name')} {sortDesc ? <FaSortDown /> : <FaSortUp />}
                  </th>
                  <th>{t('clientsInstructions.emails', 'Emails')}</th>
                  <th>{t('clientsInstructions.instructions')}</th>
                  <th>{t('clientsInstructions.reference')}</th>
                  <th>{t('common.automation')}</th>
                  <th>{t('clientsList.disabledCarriers', 'Transp. désactivés')}</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {paginatedClients.map(client => (
                  <tr
                    key={client.id}
                    className="client-row"
                    onClick={() => handleClientClick(client)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClientClick(client);
                      }
                    }}
                  >
                    <td className="cell-client" title={client.name}>
                      <span className="client-name-text">{client.name}</span>
                      {client.client_id && <span className="client-id-chip">{client.client_id}</span>}
                    </td>
                    <td>
                      {client.emails?.length > 0
                        ? <span className="cell-num">{client.emails.length}</span>
                        : <span className="muted-dash">–</span>}
                    </td>
                    <td>
                      {client.instructions ? (
                        <span className="flag-ok" title={t('clientsList.instructionsSet', 'Instructions renseignées')}>
                          <FaCheck aria-hidden="true" />
                        </span>
                      ) : (
                        <span className="flag-warn">
                          <span className="warn-dot" aria-hidden="true" />
                          {t('clientsList.missing', 'manquantes')}
                        </span>
                      )}
                    </td>
                    <td>
                      {client.reference && client.reference.trim() !== '' ? (
                        <span className="flag-ok" title={t('clientsList.referenceSet', 'Référence renseignée')}>
                          <FaCheck aria-hidden="true" />
                        </span>
                      ) : (
                        <span className="muted-dash">–</span>
                      )}
                    </td>
                    <td>
                      <span className={`auto-state ${client.automation ? 'on' : 'off'}`}>
                        <span className="state-dot" aria-hidden="true" />
                        {client.automation ? t('common.on') : t('common.off')}
                      </span>
                    </td>
                    <td>
                      {client.disabled_carriers?.length > 0 ? (
                        <span className="carriers-chip">
                          {client.disabled_carriers.length === 1
                            ? t('clientsList.oneDisabled', '1 désactivé')
                            : t('clientsList.nDisabled', '{{total}} désactivés', { total: client.disabled_carriers.length })}
                        </span>
                      ) : (
                        <span className="muted-dash">–</span>
                      )}
                    </td>
                    <td className="cell-chevron">
                      <FaChevronRight aria-hidden="true" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="clients-empty-state">
            {searchQuery ? t('common.noSearchResults') : t('clientsInstructions.noClients')}
          </div>
        )}

        {totalPages > 1 && paginatedClients.length > 0 && (
          <div className="clients-card-footer">
            {renderPager()}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modalai" onClick={handleCloseCreateModal}>
          <div
            className="modal-content client-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-client-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="client-modal-header">
              <h3 id="create-client-title">{t('clientsInstructions.createNew')}</h3>
              <button
                type="button"
                className="client-modal-close"
                onClick={handleCloseCreateModal}
                aria-label={t('common.cancel', 'Annuler')}
              >
                <FaTimes />
              </button>
            </div>

            <div className="client-modal-body">
              {createError && (
                <div className="client-modal-alert">{createError}</div>
              )}

              <h4 className="form-section-title">{t('clientEDNDetail.sectionIdentity', 'Identité')}</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="new-client-name">{t('clientsInstructions.name')} *</label>
                  <input
                    id="new-client-name"
                    type="text"
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    className="ai-agent-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-client-id">{t('clientsInstructions.clientId')}</label>
                  <input
                    id="new-client-id"
                    type="text"
                    value={newClient.client_id}
                    onChange={(e) => setNewClient({ ...newClient, client_id: e.target.value })}
                    className="ai-agent-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('clientEDNDetail.aliases', 'Alias')}</label>
                {newClient.aliases.map((alias, index) => (
                  <div key={index} className="email-input-group">
                    <input
                      type="text"
                      value={alias}
                      onChange={(e) => {
                        const updated = [...newClient.aliases];
                        updated[index] = e.target.value;
                        setNewClient({ ...newClient, aliases: updated });
                      }}
                      placeholder={t('clientEDNDetail.aliasPlaceholder', "Nom d'alias")}
                      className="ai-agent-input"
                    />
                    <button
                      type="button"
                      className="client-modal-remove-btn"
                      onClick={() => {
                        const updated = [...newClient.aliases];
                        updated.splice(index, 1);
                        setNewClient({ ...newClient, aliases: updated });
                      }}
                      aria-label={t('common.delete', 'Supprimer')}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="client-modal-add-btn"
                  onClick={() => setNewClient({ ...newClient, aliases: [...newClient.aliases, ''] })}
                >
                  <FaPlus /> {t('clientEDNDetail.addAlias', 'Ajouter un alias')}
                </button>
              </div>

              <h4 className="form-section-title">{t('clientEDNDetail.sectionDetection', 'Détection des e-mails')}</h4>
              <div className="form-group" data-tour="client-emails">
                <label>{t('clientsInstructions.emails', 'Emails')}</label>
                <p className="email-hint">{t('clientsInstructions.emailsHint', "Sert à identifier les e-mails entrants de ce client — adresses complètes ou domaines (ex. brand.com).")}</p>
                {newClient.emails.map((email, index) => (
                  <div key={index} className="email-input-group">
                    <input
                      type="text"
                      value={email}
                      onChange={(e) => {
                        const updated = [...newClient.emails];
                        updated[index] = e.target.value;
                        setNewClient({ ...newClient, emails: updated });
                      }}
                      placeholder={t('clientsInstructions.emailPlaceholder', 'Adresse ou domaine (ex. brand.com)')}
                      className="ai-agent-input"
                    />
                    <button
                      type="button"
                      className="client-modal-remove-btn"
                      onClick={() => {
                        const updated = [...newClient.emails];
                        updated.splice(index, 1);
                        setNewClient({ ...newClient, emails: updated });
                      }}
                      aria-label={t('common.delete', 'Supprimer')}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="client-modal-add-btn"
                  onClick={() => setNewClient({ ...newClient, emails: [...newClient.emails, ''] })}
                >
                  <FaPlus /> {t('clientsInstructions.addEmail', 'Ajouter un email')}
                </button>
              </div>

              <h4 className="form-section-title">{t('clientEDNDetail.sectionInstructions', 'Instructions IA')}</h4>
              <div className="form-group">
                <label htmlFor="new-client-instructions">{t('clientsInstructions.instructions')}</label>
                <textarea
                  id="new-client-instructions"
                  value={newClient.instructions}
                  onChange={(e) => setNewClient({ ...newClient, instructions: e.target.value })}
                  rows="5"
                  className="ai-agent-textarea"
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="new-client-reference">{t('clientsInstructions.reference')}</label>
                  <input
                    id="new-client-reference"
                    type="text"
                    value={newClient.reference}
                    onChange={(e) => setNewClient({ ...newClient, reference: e.target.value })}
                    className="ai-agent-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-client-effitrace">{t('clientEDNDetail.effitraceUrl', 'URL Effitrace')}</label>
                  <input
                    id="new-client-effitrace"
                    type="url"
                    value={newClient.effitrace_url}
                    onChange={(e) => setNewClient({ ...newClient, effitrace_url: e.target.value })}
                    placeholder="https://..."
                    className="ai-agent-input"
                  />
                </div>
              </div>

              <h4 className="form-section-title">{t('clientEDNDetail.sectionAutomation', 'Automatisation & transporteurs')}</h4>
              <div className="form-group" data-tour="client-automation">
                <label className="client-modal-inline-toggle">
                  <input
                    type="checkbox"
                    checked={newClient.automation}
                    onChange={(e) => setNewClient({ ...newClient, automation: e.target.checked })}
                  />
                  {t('clientEDNDetail.automationEnabled', 'Automatisation activée pour ce client')}
                </label>
              </div>
              <div className="form-group">
                <label>{t('clientsList.disabledCarriers', 'Transp. désactivés')}</label>
                <small className="form-help-text">
                  {t('clientEDNDetail.disabledCarriersHelp', "Les transporteurs désactivés ne sont pas utilisés par l'automatisation pour ce client.")}
                </small>
                {carriers.length > 0 ? (
                  <div className="disabled-carriers-list">
                    {carriers.map((carrier) => {
                      const isDisabled = newClient.disabled_carriers.includes(carrier.name);
                      return (
                        <label
                          key={carrier.id}
                          className={`carrier-checkbox-item ${isDisabled ? 'carrier-disabled' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isDisabled}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...newClient.disabled_carriers, carrier.name]
                                : newClient.disabled_carriers.filter((c) => c !== carrier.name);
                              setNewClient({ ...newClient, disabled_carriers: updated });
                            }}
                          />
                          <FaTruck className="carrier-icon" />
                          <span className="carrier-name">{carrier.name}</span>
                          {isDisabled && <FaBan className="carrier-ban-icon" />}
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="no-carriers-text">{t('clientEDNDetail.noCarriersAvailable', 'Aucun transporteur disponible')}</p>
                )}
              </div>

              <h4 className="form-section-title">{t('clientEDNDetail.sectionSouffrance', 'Notifications souffrance')}</h4>
              <div className="form-group">
                <label htmlFor="new-client-default-email">{t('clientEDNDetail.defaultEmail', 'Email de notification par défaut')}</label>
                <small className="form-help-text">
                  {t('clientEDNDetail.emailRoutingHelp', 'Adresse qui reçoit les notifications de souffrance pour ce client.')}
                </small>
                <input
                  id="new-client-default-email"
                  type="email"
                  value={newClient.default_email}
                  onChange={(e) => setNewClient({ ...newClient, default_email: e.target.value })}
                  placeholder={t('clientsInstructions.emailPlaceholder', 'Adresse email')}
                  className="ai-agent-input"
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="new-client-souffrance-lang">{t('clientEDNDetail.souffranceLanguage', 'Langue des messages')}</label>
                  <select
                    id="new-client-souffrance-lang"
                    value={newClient.souffrance_language}
                    onChange={(e) => setNewClient({ ...newClient, souffrance_language: e.target.value })}
                    className="ai-agent-input"
                  >
                    <option value="">{t('clientEDNDetail.souffranceLanguageDefault', 'Par défaut (français)')}</option>
                    {SOUFFRANCE_LANGUAGES.map((lang) => (
                      <option key={lang} value={lang}>
                        {t(`clientEDNDetail.languages.${lang}`, lang)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="new-client-souffrance-instructions">{t('clientEDNDetail.souffranceInstructions', 'Instructions souffrance')}</label>
                <small className="form-help-text">
                  {t('clientEDNDetail.souffranceHelp', "Spécifiques à ce client : la langue remplace la langue générale, les instructions s'ajoutent aux instructions générales.")}
                </small>
                <textarea
                  id="new-client-souffrance-instructions"
                  value={newClient.souffrance_instructions}
                  onChange={(e) => setNewClient({ ...newClient, souffrance_instructions: e.target.value })}
                  rows="3"
                  placeholder={t('clientEDNDetail.souffranceInstructionsPlaceholder', 'Ex. toujours mentionner notre numéro de support...')}
                  className="ai-agent-textarea"
                />
              </div>
            </div>

            <div className="client-modal-footer">
              <button
                type="button"
                className="u-btn u-btn--secondary"
                onClick={handleCloseCreateModal}
                disabled={creating}
              >
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                type="button"
                className="u-btn u-btn--primary"
                data-tour="client-create"
                onClick={handleCreateClient}
                disabled={creating || !newClient.name.trim()}
              >
                <FaSave /> {creating ? t('common.saving', 'Enregistrement...') : t('common.create', 'Créer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsInstructions;
